import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { Palette, MoveDiagonal2, Type, RotateCcw, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService, DashboardEmbedInfo } from '@/api/apiService';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export interface WidgetColorSettings {
  header_bg: string;
  header_fg: string;
  messages_bg: string;
  user_bg: string;
  user_fg: string;
  bot_bg: string;
  bot_fg: string;
  bot_border: string;
  input_border: string;
  link_color: string;
  link_hover: string;
  typing_dot: string;
  launcher_bg: string;
  launcher_fg: string;
  btn_slot_bg: string;
  btn_slot_fg: string;
  btn_slot_border: string;
  btn_refresh_bg: string;
  btn_refresh_fg: string;
  btn_cta_bg: string;
  btn_cta_fg: string;
  select_bg: string;
  select_fg: string;
  select_border: string;
}

export type WidgetThemeName = 'default' | 'light' | 'dark' | 'ocean';

export interface WidgetSettings {
  theme: WidgetThemeName;
  title: string;
  position: 'left' | 'right';
  offsetBottom: number;
  offsetSide: number;
  widgetGap: number;
  colors: WidgetColorSettings;
}

const DEFAULT_COLORS: WidgetColorSettings = {
  header_bg: '#000000',
  header_fg: '#ffffff',
  messages_bg: '#f7f7f7',
  user_bg: '#000000',
  user_fg: '#ffffff',
  bot_bg: '#ffffff',
  bot_fg: '#111111',
  bot_border: '#dddddd',
  input_border: '#dddddd',
  link_color: '#0066cc',
  link_hover: '#004499',
  typing_dot: '#999999',
  launcher_bg: '#000000',
  launcher_fg: '#ffffff',
  btn_slot_bg: '#f0f0f0',
  btn_slot_fg: '#000000',
  btn_slot_border: '#c9c9c9',
  btn_refresh_bg: '#000000',
  btn_refresh_fg: '#ffffff',
  btn_cta_bg: '#000000',
  btn_cta_fg: '#ffffff',
  select_bg: '#ffffff',
  select_fg: '#111111',
  select_border: '#dddddd',
};

const DARK_COLORS: WidgetColorSettings = {
  header_bg: '#111827',
  header_fg: '#f9fafb',
  messages_bg: '#1f2937',
  user_bg: '#3b82f6',
  user_fg: '#ffffff',
  bot_bg: '#374151',
  bot_fg: '#f3f4f6',
  bot_border: '#4b5563',
  input_border: '#4b5563',
  link_color: '#93c5fd',
  link_hover: '#bfdbfe',
  typing_dot: '#9ca3af',
  launcher_bg: '#3b82f6',
  launcher_fg: '#ffffff',
  btn_slot_bg: '#4b5563',
  btn_slot_fg: '#f3f4f6',
  btn_slot_border: '#6b7280',
  btn_refresh_bg: '#3b82f6',
  btn_refresh_fg: '#ffffff',
  btn_cta_bg: '#3b82f6',
  btn_cta_fg: '#ffffff',
  select_bg: '#374151',
  select_fg: '#f3f4f6',
  select_border: '#4b5563',
};

const OCEAN_COLORS: WidgetColorSettings = {
  header_bg: '#0c4a6e',
  header_fg: '#f0f9ff',
  messages_bg: '#e0f2fe',
  user_bg: '#0369a1',
  user_fg: '#ffffff',
  bot_bg: '#ffffff',
  bot_fg: '#0c4a6e',
  bot_border: '#bae6fd',
  input_border: '#bae6fd',
  link_color: '#0284c7',
  link_hover: '#0369a1',
  typing_dot: '#64748b',
  launcher_bg: '#0369a1',
  launcher_fg: '#f0f9ff',
  btn_slot_bg: '#e0f2fe',
  btn_slot_fg: '#0c4a6e',
  btn_slot_border: '#7dd3fc',
  btn_refresh_bg: '#0369a1',
  btn_refresh_fg: '#f0f9ff',
  btn_cta_bg: '#0369a1',
  btn_cta_fg: '#f0f9ff',
  select_bg: '#ffffff',
  select_fg: '#0c4a6e',
  select_border: '#bae6fd',
};

const THEME_PRESETS: Record<WidgetThemeName, WidgetColorSettings> = {
  default: DEFAULT_COLORS,
  light: DEFAULT_COLORS,
  dark: DARK_COLORS,
  ocean: OCEAN_COLORS,
};

const THEME_LABELS: Record<WidgetThemeName, string> = {
  default: 'Default',
  light: 'Light',
  dark: 'Dark',
  ocean: 'Ocean',
};

const DEFAULT_SETTINGS: WidgetSettings = {
  theme: 'default',
  title: 'Assistant',
  position: 'right',
  offsetBottom: 20,
  offsetSide: 20,
  widgetGap: 10,
  colors: { ...DEFAULT_COLORS },
};

const COLOR_GROUPS: { title: string; fields: { key: keyof WidgetColorSettings; label: string }[] }[] = [
  {
    title: 'Header',
    fields: [
      { key: 'header_bg', label: 'Background' },
      { key: 'header_fg', label: 'Text' },
    ],
  },
  { title: 'Messages area', fields: [{ key: 'messages_bg', label: 'Background' }] },
  {
    title: 'Your visitor\u2019s bubble',
    fields: [
      { key: 'user_bg', label: 'Background' },
      { key: 'user_fg', label: 'Text' },
    ],
  },
  {
    title: 'Bot\u2019s bubble',
    fields: [
      { key: 'bot_bg', label: 'Background' },
      { key: 'bot_fg', label: 'Text' },
      { key: 'bot_border', label: 'Border' },
    ],
  },
  { title: 'Input box', fields: [{ key: 'input_border', label: 'Border' }] },
  {
    title: 'Links',
    fields: [
      { key: 'link_color', label: 'Color' },
      { key: 'link_hover', label: 'Hover color' },
    ],
  },
  { title: 'Typing indicator', fields: [{ key: 'typing_dot', label: 'Dot color' }] },
  {
    title: 'Quick-reply chips',
    fields: [
      { key: 'btn_slot_bg', label: 'Background' },
      { key: 'btn_slot_fg', label: 'Text' },
      { key: 'btn_slot_border', label: 'Border' },
    ],
  },
  {
    title: 'Refresh button',
    fields: [
      { key: 'btn_refresh_bg', label: 'Background' },
      { key: 'btn_refresh_fg', label: 'Text' },
    ],
  },
  {
    title: 'Call-to-action button',
    fields: [
      { key: 'btn_cta_bg', label: 'Background' },
      { key: 'btn_cta_fg', label: 'Text' },
    ],
  },
  {
    title: 'Dropdown menus',
    fields: [
      { key: 'select_bg', label: 'Background' },
      { key: 'select_fg', label: 'Text' },
      { key: 'select_border', label: 'Border' },
    ],
  },
];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function isValidHex(value: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function mergeSettings(custom: Record<string, any> | null | undefined): WidgetSettings {
  if (!custom || typeof custom !== 'object' || Object.keys(custom).length === 0) {
    return { ...DEFAULT_SETTINGS, colors: { ...DEFAULT_COLORS } };
  }
  const theme: WidgetThemeName = ['default', 'light', 'dark', 'ocean'].includes(custom.theme) ? custom.theme : 'default';
  const baseColors = THEME_PRESETS[theme];
  const colors = { ...baseColors, ...(custom.colors && typeof custom.colors === 'object' ? custom.colors : {}) };
  return {
    theme,
    title: typeof custom.title === 'string' && custom.title.trim() ? custom.title : 'Assistant',
    position: custom.position === 'left' ? 'left' : 'right',
    offsetBottom: Number.isFinite(custom.offsetBottom) ? clamp(custom.offsetBottom, 0, 100) : 20,
    offsetSide: Number.isFinite(custom.offsetSide) ? clamp(custom.offsetSide, 0, 100) : 20,
    widgetGap: Number.isFinite(custom.widgetGap) ? clamp(custom.widgetGap, 0, 40) : 10,
    colors,
  };
}

function buildScriptSnippet(apiBase: string, websiteId: number, settings: WidgetSettings): string {
  const attrs: string[] = [`src="${apiBase}/static/chatbot.js"`, `data-website-id="${websiteId}"`, `data-chat-theme="${settings.theme}"`];
  if (settings.title.trim()) attrs.push(`data-chat-title="${escapeAttr(settings.title.trim())}"`);
  attrs.push(`data-chat-position="${settings.position}"`);
  attrs.push(`data-chat-offset-bottom="${settings.offsetBottom}"`);
  attrs.push(`data-chat-offset-side="${settings.offsetSide}"`);
  attrs.push(`data-chat-widget-gap="${settings.widgetGap}"`);
  for (const [key, value] of Object.entries(settings.colors)) {
    attrs.push(`data-chat-${key.replace(/_/g, '-')}="${value}"`);
  }
  attrs.push('defer');
  return `<script ${attrs.join(' ')}></script>`;
}

function buildIframeSnippet(apiBase: string, websiteId: number, settings: WidgetSettings): string {
  const q = new URLSearchParams();
  q.set('website_id', String(websiteId));
  q.set('theme', settings.theme);
  if (settings.title.trim()) q.set('title', settings.title.trim());
  for (const [key, value] of Object.entries(settings.colors)) {
    if (key === 'launcher_bg' || key === 'launcher_fg') continue;
    q.set(key, value);
  }
  const url = `${apiBase}/chat-ui?${q.toString()}`;
  return `<iframe src="${url}" title="AI Assistant" width="350" height="500" style="border:none;border-radius:12px;" allow="clipboard-write"></iframe>`;
}

export type PreviewScenario = 'chat' | 'booking';

function buildPreviewSrc(apiBase: string, settings: WidgetSettings, scenario: PreviewScenario): string {
  const q = new URLSearchParams();
  q.set('demo', '1');
  q.set('demo_scenario', scenario);
  q.set('theme', settings.theme);
  if (settings.title.trim()) q.set('title', settings.title.trim());
  for (const [key, value] of Object.entries(settings.colors)) {
    if (key === 'launcher_bg' || key === 'launcher_fg') continue;
    q.set(key, value);
  }
  return `${apiBase}/chat-ui?${q.toString()}`;
}

function ColorField({
  label,
  value,
  onChange,
  onReset,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onReset?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <div className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{label}</span>
        {onReset ? (
          <button
            type="button"
            onClick={onReset}
            title="Reset to theme default"
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="h-7 w-7 shrink-0 rounded-md border shadow-sm"
          style={{ background: isValidHex(value) ? value : '#ffffff' }}
          aria-label={`Pick ${label} color`}
        />
        <HexColorInput
          color={value}
          onChange={onChange}
          prefixed
          className="h-7 w-[74px] shrink-0 rounded-md border border-input bg-background px-1.5 text-xs font-mono uppercase text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      {open ? (
        <div className="absolute right-0 top-full z-50 mt-1 rounded-md border bg-popover p-2 shadow-lg">
          <HexColorPicker color={isValidHex(value) ? value : '#ffffff'} onChange={onChange} />
        </div>
      ) : null}
    </div>
  );
}

function WidgetPreview({
  settings,
  previewSrc,
  onLauncherDrag,
  onDragStart,
  onDragEnd,
}: {
  settings: WidgetSettings;
  previewSrc: string;
  onLauncherDrag: (next: { position: 'left' | 'right'; offsetBottom: number; offsetSide: number }) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const launcherSize = 52;
  const half = launcherSize / 2;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    onDragStart?.();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || !frameRef.current) return;
    const rect = frameRef.current.getBoundingClientRect();
    const x = clamp(e.clientX - rect.left, 0, rect.width);
    const y = clamp(e.clientY - rect.top, 0, rect.height);
    const side: 'left' | 'right' = x < rect.width / 2 ? 'left' : 'right';
    const offsetSide = clamp(Math.round(side === 'left' ? x - half : rect.width - x - half), 0, 100);
    const offsetBottom = clamp(Math.round(rect.height - y - half), 0, 100);
    onLauncherDrag({ position: side, offsetBottom, offsetSide });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    setIsDragging(false);
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    onDragEnd?.();
  };

  const frameHeight = 640;
  const topBarHeight = 28;
  const topMargin = 14;
  const chatWidth = 320;
  // Keep the chat window from ever getting clipped at the top of the mock
  // page, no matter how far the launcher has been dragged down/up.
  const chatHeight = clamp(
    frameHeight - topBarHeight - topMargin - (settings.offsetBottom + launcherSize + settings.widgetGap),
    260,
    460
  );

  return (
    <div
      ref={frameRef}
      className="relative w-full select-none overflow-hidden rounded-lg border bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900"
      style={{ height: frameHeight }}
    >
      <div className="flex h-7 items-center gap-1.5 border-b bg-white/70 px-3 dark:bg-slate-900/70">
        <span className="h-2 w-2 rounded-full bg-red-400" />
        <span className="h-2 w-2 rounded-full bg-yellow-400" />
        <span className="h-2 w-2 rounded-full bg-green-400" />
        <span className="ml-2 truncate text-[10px] text-muted-foreground">yoursite.com</span>
      </div>

      <iframe
        key={previewSrc}
        title="Chat widget preview"
        src={previewSrc}
        className="absolute rounded-xl border-0 shadow-xl transition-[left,right,bottom,width,height] duration-150"
        style={{
          width: chatWidth,
          height: chatHeight,
          bottom: settings.offsetBottom + launcherSize + settings.widgetGap,
          [settings.position]: settings.offsetSide,
          pointerEvents: isDragging ? 'none' : 'auto',
        }}
      />

      {isDragging ? (
        <div
          className="absolute z-10 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[11px] font-medium text-background shadow-lg"
          style={{
            bottom: settings.offsetBottom + launcherSize + 8,
            [settings.position]: settings.offsetSide,
          }}
        >
          {settings.position === 'left' ? 'Bottom left' : 'Bottom right'} · {settings.offsetBottom}px up ·{' '}
          {settings.offsetSide}px in
        </div>
      ) : null}

      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={`absolute flex cursor-grab items-center justify-center rounded-full text-xl shadow-lg transition-[left,right,bottom] duration-150 active:cursor-grabbing ${
          isDragging ? 'ring-4 ring-primary/40' : ''
        }`}
        style={{
          width: launcherSize,
          height: launcherSize,
          bottom: settings.offsetBottom,
          [settings.position]: settings.offsetSide,
          background: settings.colors.launcher_bg,
          color: settings.colors.launcher_fg,
        }}
      >
        💬
      </div>
    </div>
  );
}

interface WidgetCustomizerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: number;
  embedInfo: DashboardEmbedInfo;
  onSaved: (info: DashboardEmbedInfo) => void;
}

export default function WidgetCustomizerDialog({ open, onOpenChange, siteId, embedInfo, onSaved }: WidgetCustomizerDialogProps) {
  const [settings, setSettings] = useState<WidgetSettings>(() => mergeSettings(embedInfo.widget_settings_custom));
  const [activeTab, setActiveTab] = useState('colors');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewScenario, setPreviewScenario] = useState<PreviewScenario>('chat');

  useEffect(() => {
    if (open) {
      setSettings(mergeSettings(embedInfo.widget_settings_custom));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, embedInfo.site_id]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setPreviewSrc(buildPreviewSrc(embedInfo.api_base_url, settings, previewScenario));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [embedInfo.api_base_url, settings, previewScenario]);

  const setColor = (key: keyof WidgetColorSettings, value: string) => {
    setSettings((prev) => ({ ...prev, colors: { ...prev.colors, [key]: value } }));
  };

  const resetColor = (key: keyof WidgetColorSettings) => {
    setSettings((prev) => ({ ...prev, colors: { ...prev.colors, [key]: THEME_PRESETS[prev.theme][key] } }));
  };

  const applyPreset = (theme: WidgetThemeName) => {
    setSettings((prev) => ({ ...prev, theme, colors: { ...THEME_PRESETS[theme] } }));
  };

  const handleLauncherDrag = (next: { position: 'left' | 'right'; offsetBottom: number; offsetSide: number }) => {
    setSettings((prev) => ({ ...prev, ...next }));
  };

  const handleDragStart = () => {
    setActiveTab('position');
  };

  const handleDragEnd = () => {
    toast.success('Position updated — see the Position & Size tab and the generated snippet.', { id: 'widget-drag-hint' });
  };

  const scriptPreviewSnippet = useMemo(
    () => buildScriptSnippet(embedInfo.api_base_url, embedInfo.website_id, settings),
    [embedInfo.api_base_url, embedInfo.website_id, settings]
  );

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const scriptSnippet = buildScriptSnippet(embedInfo.api_base_url, embedInfo.website_id, settings);
      const iframeSnippet = buildIframeSnippet(embedInfo.api_base_url, embedInfo.website_id, settings);
      const updated = await apiService.dashboardUpdateEmbedInfo(siteId, {
        script_snippet: scriptSnippet,
        iframe_snippet: iframeSnippet,
        widget_settings: settings,
      });
      onSaved(updated);
      toast.success('Widget appearance saved');
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to save widget appearance');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetAll = () => {
    setSettings({ ...DEFAULT_SETTINGS, colors: { ...DEFAULT_COLORS } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl gap-0 overflow-hidden p-0 sm:max-h-[92vh]">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px]">
          <div className="flex max-h-[92vh] flex-col overflow-y-auto p-6">
            <DialogHeader className="mb-4">
              <DialogTitle className="flex items-center gap-2">
                <Palette className="h-4 w-4" /> Chat Widget Designer
              </DialogTitle>
              <DialogDescription>
                Customize colors, text and where the chat button sits on your site. Changes only apply after you save.
              </DialogDescription>
            </DialogHeader>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="colors">
                  <Palette className="mr-1.5 h-3.5 w-3.5" /> Theme &amp; Colors
                </TabsTrigger>
                <TabsTrigger value="position">
                  <MoveDiagonal2 className="mr-1.5 h-3.5 w-3.5" /> Position &amp; Size
                </TabsTrigger>
                <TabsTrigger value="text">
                  <Type className="mr-1.5 h-3.5 w-3.5" /> Text
                </TabsTrigger>
              </TabsList>

              <TabsContent value="colors" className="space-y-5">
                <div>
                  <Label className="mb-2 block text-xs font-medium text-muted-foreground">Theme preset</Label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {(Object.keys(THEME_PRESETS) as WidgetThemeName[]).map((theme) => (
                      <button
                        key={theme}
                        type="button"
                        onClick={() => applyPreset(theme)}
                        className={`flex flex-col items-center gap-1.5 rounded-md border p-2 text-xs transition-colors ${
                          settings.theme === theme ? 'border-primary ring-1 ring-primary' : 'hover:bg-accent'
                        }`}
                      >
                        <span className="flex gap-1">
                          <span className="h-4 w-4 rounded-full border" style={{ background: THEME_PRESETS[theme].header_bg }} />
                          <span className="h-4 w-4 rounded-full border" style={{ background: THEME_PRESETS[theme].bot_bg }} />
                          <span className="h-4 w-4 rounded-full border" style={{ background: THEME_PRESETS[theme].user_bg }} />
                        </span>
                        {THEME_LABELS[theme]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-md border p-3">
                    <ColorField
                      label="Header background"
                      value={settings.colors.header_bg}
                      onChange={(v) => setColor('header_bg', v)}
                      onReset={() => resetColor('header_bg')}
                    />
                  </div>
                  <div className="rounded-md border p-3">
                    <ColorField
                      label="Bot reply bubble"
                      value={settings.colors.bot_bg}
                      onChange={(v) => setColor('bot_bg', v)}
                      onReset={() => resetColor('bot_bg')}
                    />
                  </div>
                  <div className="rounded-md border p-3">
                    <ColorField
                      label="Visitor bubble"
                      value={settings.colors.user_bg}
                      onChange={(v) => setColor('user_bg', v)}
                      onReset={() => resetColor('user_bg')}
                    />
                  </div>
                  <div className="rounded-md border p-3">
                    <ColorField
                      label="Launcher button"
                      value={settings.colors.launcher_bg}
                      onChange={(v) => setColor('launcher_bg', v)}
                      onReset={() => resetColor('launcher_bg')}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowAdvanced((s) => !s)}
                  className="flex w-full items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm font-medium"
                >
                  Fine-tune every color
                  {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {showAdvanced ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {COLOR_GROUPS.map((group) => (
                      <div key={group.title} className="space-y-2 rounded-md border p-3">
                        <p className="text-xs font-semibold">{group.title}</p>
                        {group.fields.map((field) => (
                          <ColorField
                            key={field.key}
                            label={field.label}
                            value={settings.colors[field.key]}
                            onChange={(v) => setColor(field.key, v)}
                            onReset={() => resetColor(field.key)}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                ) : null}
              </TabsContent>

              <TabsContent value="position" className="space-y-5">
                <p className="text-sm text-muted-foreground">
                  Drag the chat bubble in the preview to reposition it, or fine-tune with the controls below.
                </p>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground">Corner</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={settings.position === 'left' ? 'default' : 'outline'}
                        onClick={() => setSettings((prev) => ({ ...prev, position: 'left' }))}
                      >
                        Bottom left
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={settings.position === 'right' ? 'default' : 'outline'}
                        onClick={() => setSettings((prev) => ({ ...prev, position: 'right' }))}
                      >
                        Bottom right
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-muted-foreground">Bottom offset</Label>
                      <span className="text-xs font-mono text-muted-foreground">{settings.offsetBottom}px</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={settings.offsetBottom}
                      onChange={(e) => setSettings((prev) => ({ ...prev, offsetBottom: Number(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-muted-foreground">Side offset</Label>
                      <span className="text-xs font-mono text-muted-foreground">{settings.offsetSide}px</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={settings.offsetSide}
                      onChange={(e) => setSettings((prev) => ({ ...prev, offsetSide: Number(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-muted-foreground">Gap to launcher</Label>
                      <span className="text-xs font-mono text-muted-foreground">{settings.widgetGap}px</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={40}
                      value={settings.widgetGap}
                      onChange={(e) => setSettings((prev) => ({ ...prev, widgetGap: Number(e.target.value) }))}
                      className="w-full"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="text" className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground">Chat header title</Label>
                  <Input
                    value={settings.title}
                    onChange={(e) => setSettings((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Assistant"
                    maxLength={60}
                  />
                  <p className="text-xs text-muted-foreground">Shown at the top of the chat window.</p>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" size="sm" onClick={handleResetAll} disabled={isSaving}>
                Reset to defaults
              </Button>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Save widget appearance
              </Button>
            </DialogFooter>
          </div>

          <div className="relative z-0 flex flex-col gap-2 border-t bg-muted/30 p-4 pt-11 lg:border-l lg:border-t-0">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold">Live preview</p>
              <div className="flex gap-1 rounded-md border bg-background p-0.5">
                <button
                  type="button"
                  onClick={() => setPreviewScenario('chat')}
                  className={`rounded-sm px-2 py-1 text-[11px] font-medium transition-colors ${
                    previewScenario === 'chat' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  Chat
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewScenario('booking')}
                  className={`rounded-sm px-2 py-1 text-[11px] font-medium transition-colors ${
                    previewScenario === 'booking' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'
                  }`}
                >
                  Booking &amp; slots
                </button>
              </div>
            </div>
            {previewSrc ? (
              <WidgetPreview
                settings={settings}
                previewSrc={previewSrc}
                onLauncherDrag={handleLauncherDrag}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              />
            ) : (
              <div className="flex h-[640px] w-full items-center justify-center rounded-lg border bg-muted/40">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              This is a live demo — no messages are sent to your real chatbot or API. Drag the bubble to move it, or
              switch to &quot;Booking &amp; slots&quot; to preview the time-slot picker.
            </p>
            <details className="mt-1 text-[11px] text-muted-foreground">
              <summary className="cursor-pointer select-none">Preview generated snippet</summary>
              <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 font-mono text-[10px]">
                {scriptPreviewSnippet}
              </pre>
            </details>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
